"use client";
import { useState, useEffect, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/app/_module/components/ui/tabs";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { Button } from "@/app/_module/components/ui/button";
import { Lock, Phone, Loader2, Send, History, CreditCard } from "lucide-react";
import type {
  FaxDirectionFilter,
  FaxStatusFilter,
} from "@/app/_module/types/fax";

import { FaxFilters } from "../communication/_modules/components/FaxFilters";
import { FaxHistory } from "../communication/_modules/components/FaxHistory";
import { SendFax } from "../communication/_modules/components/SendFax";
import { useFaxAccess } from "@/app/_module/hooks/useFaxAccess";
import { showToast } from "@/app/_module/utils/show-toast";

import { useInfiniteFaxHistory } from "./_modules/hooks";
import type { FaxFilters as FaxStatus } from "./_modules/types/types";

const Fax = function () {
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    hasAccess,
    planName,
    reason,
    faxNumber,
    requiresPayment,
    isLoading: isLoadingAccess,
    provisionFaxNumber,
    isProvisioning,
    startFaxCheckout,
    isCheckingOut,
    fulfillFaxCheckout,
    isFulfilling,
  } = useFaxAccess();

  // Get initial tab from URL or default to "send"
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    // 'received' tab was merged into History — redirect stale links there.
    return t === 'received' ? 'history' : (t || 'send');
  });

  // Handle tab change - update URL to persist tab selection
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    const params = new URLSearchParams(searchParams.toString());
    params.set('tab', value);
    router.replace(`?${params.toString()}`, { scroll: false });
  };

  // Sync tab state with URL on initial load and URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') === 'received' ? 'history' : searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Handle Stripe checkout redirect — fulfill fax number after successful payment
  const fulfillAttempted = useRef(false);
  useEffect(() => {
    const status = searchParams.get('status');
    const sessionId = searchParams.get('session_id');

    if (status === 'success' && sessionId && !fulfillAttempted.current) {
      fulfillAttempted.current = true;
      fulfillFaxCheckout(sessionId)
        .then(() => {
          showToast('Payment successful! Your fax number is being provisioned.', 'success');
        })
        .catch(() => {
          showToast('Payment received. Your fax number will be provisioned shortly.', 'success');
        })
        .finally(() => {
          // Clean URL params
          const params = new URLSearchParams(searchParams.toString());
          params.delete('status');
          params.delete('session_id');
          const cleanUrl = params.toString() ? `?${params.toString()}` : window.location.pathname;
          router.replace(cleanUrl, { scroll: false });
        });
    } else if (status === 'cancelled') {
      showToast('Checkout was cancelled. You can try again when ready.', 'info');
      const params = new URLSearchParams(searchParams.toString());
      params.delete('status');
      params.delete('session_id');
      const cleanUrl = params.toString() ? `?${params.toString()}` : window.location.pathname;
      router.replace(cleanUrl, { scroll: false });
    }
  }, [searchParams]);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [faxDirectionFilter, setFaxDirectionFilter] =
    useState<FaxDirectionFilter>("all");
  const [faxStatusFilter, setFaxStatusFilter] =
    useState<FaxStatusFilter>("all");

  // Debounce search query to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Use infinite hook (server-side status, client search)
  const serverStatus: "all" | FaxStatus =
    faxStatusFilter === "received" ? "all" : (faxStatusFilter as FaxStatus);

  // Fetch all faxes for count calculation (use debounced search)
  const { faxes: allFaxes, isLoading, error } = useInfiniteFaxHistory({
    search: debouncedSearchQuery,
    status: "all",
    direction: "all",
    limit: 20,
  });

  // Show loading state
  if (isLoadingAccess) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-foreground">Fax Center</h1>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  // Show upgrade prompt for trial users
  if (!hasAccess) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-foreground">Fax Center</h1>
        <Alert className="border-yellow-500 bg-yellow-50">
          <Lock className="h-4 w-4 text-yellow-600" />
          <AlertDescription>
            <div className="space-y-4">
              <p className="font-medium text-yellow-800">{reason}</p>
              <p className="text-sm text-yellow-700">Your current plan: <strong>{planName}</strong></p>
              <p className="text-sm text-yellow-700">
                Upgrade to Basic or Full-Fledged to get your dedicated fax number and send/receive faxes.
              </p>
              <Button
                onClick={() => router.push(
                  // Shell-aware: this page is also embedded at /m/fax
                  window.location.pathname.startsWith("/m")
                    ? "/m/settings"
                    : "/settings/subscription",
                )}
                className="bg-primary hover:bg-primary/90"
              >
                Upgrade to Access Fax
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Handle provision fax number (free — non-enterprise)
  const handleProvisionFaxNumber = async () => {
    try {
      const result = await provisionFaxNumber();
      if (result?.faxNumber) {
        showToast(`Fax number ${result.faxNumber} has been provisioned!`, 'success');
      }
    } catch (error) {
      showToast(`Failed to provision fax number: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    }
  };

  // Handle fax number checkout (enterprise — $5/month)
  const handleFaxCheckout = async () => {
    try {
      await startFaxCheckout();
    } catch (error) {
      showToast(`Failed to start checkout: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    }
  };

  // Show fulfillment loading state after Stripe redirect
  if (isFulfilling) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-foreground">Fax Center</h1>
        <Alert className="border-green-500 bg-green-50">
          <Loader2 className="h-4 w-4 text-green-600 animate-spin" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-medium text-green-800">Setting up your fax number...</p>
              <p className="text-sm text-green-700">Payment received. Provisioning your dedicated fax number now.</p>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Show provision/purchase prompt when user has access but no fax number
  if (hasAccess && !faxNumber) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-foreground">Fax Center</h1>
        <Alert className={requiresPayment ? "border-red-500 bg-red-50" : "border-primary/50 bg-primary/5"}>
          <Phone className={`h-4 w-4 ${requiresPayment ? 'text-red-600' : 'text-primary'}`} />
          <AlertDescription>
            <div className="space-y-4">
              <p className={`font-medium ${requiresPayment ? 'text-red-800' : 'text-primary'}`}>
                {requiresPayment ? 'Get your dedicated fax number' : 'Your fax number is being set up'}
              </p>
              <p className={`text-sm ${requiresPayment ? 'text-red-700' : 'text-primary/80'}`}>
                Your current plan: <strong>{planName}</strong>
              </p>
              <p className={`text-sm ${requiresPayment ? 'text-red-700' : 'text-primary/80'}`}>
                {requiresPayment
                  ? 'The enterprise agency you belong to requires a fax number subscription at $5/month. Click below to subscribe and get your dedicated fax number.'
                  : 'Click the button below to provision your dedicated fax number. This will allow you to send and receive faxes.'
                }
              </p>
              {requiresPayment ? (
                <Button
                  onClick={handleFaxCheckout}
                  disabled={isCheckingOut}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {isCheckingOut ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Redirecting to checkout...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Buy Fax Number — $5/month
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleProvisionFaxNumber}
                  disabled={isProvisioning}
                  className="bg-primary hover:bg-primary/90"
                >
                  {isProvisioning ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Provisioning...
                    </>
                  ) : (
                    <>
                      <Phone className="h-4 w-4 mr-2" />
                      Get My Fax Number
                    </>
                  )}
                </Button>
              )}
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between" data-tour="fax-header">
        <h1 className="text-3xl font-bold text-foreground">Fax Center</h1>
      </div>

      <Tabs className="w-full" value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="inline-flex w-full max-w-xl bg-muted/50 p-1 rounded-lg" data-tour="fax-tabs">
          <TabsTrigger
            value="send"
            className="flex-1 flex items-center justify-center gap-2 rounded-md transition-all duration-200 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-sm"
            data-tour="fax-tab-send"
          >
            <Send className="h-4 w-4" />
            Send Fax
          </TabsTrigger>
          <TabsTrigger
            value="history"
            className="flex-1 flex items-center justify-center gap-2 rounded-md transition-all duration-200 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-sm"
            data-tour="fax-tab-history"
          >
            <History className="h-4 w-4" />
            Fax History
          </TabsTrigger>
        </TabsList>

        <TabsContent className="space-y-4" value="send">
          <SendFax />
        </TabsContent>

        <TabsContent className="space-y-4" value="history">
          <div className="bg-card rounded-lg shadow-sm border p-6">
            <div className="flex items-center gap-2 mb-4">
              <History className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Fax History</h2>
            </div>
            <FaxFilters
              directionFilter={faxDirectionFilter}
              onDirectionChange={setFaxDirectionFilter}
              onSearchChange={setSearchQuery}
              onStatusChange={setFaxStatusFilter}
              searchQuery={searchQuery}
              statusFilter={serverStatus}
              allFaxes={allFaxes || []}
            />

            <div className="mt-6">
              {!!isLoading && (
                <p className="text-muted-foreground">Loading fax history...</p>
              )}
              {!!error && (
                <p className="text-red-500">
                  Failed to load fax history: {String(error)}
                </p>
              )}

              {!isLoading && !error && (
                <FaxHistory direction={faxDirectionFilter} search={debouncedSearchQuery} status={faxStatusFilter} />
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Fax;
